import {Box, SxProps, Theme} from '@mui/material';
import {useCan, usePersona} from 'src/permissions/hooks';
import {EvStationOutlined} from '@mui/icons-material';
import {ChangeEvent, FC, useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {chargersApi} from 'src/api';
import {useBusinessId, useAppDispatch, useAppSelector, useDefineIsRealEstateRole} from 'src/app/hooks';
import {store} from 'src/app/store';
import {ChargerCreateFormWrapper} from 'src/components/chargers/ChargerCreateFormWrapper';
import {ChargerTable} from 'src/components/chargers/ChargerTable';
import AMPModal from 'src/components/common/AMPModal';
import {ConnectorStatusStrip} from 'src/components/chargers/ConnectorStatusStrip';
import {ChargePointStatus, ChargerSortFieldEnum, ConnectorStatusEnum} from 'src/enums';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {ChargePointSearchRequestData, ListParamsType} from 'src/types';
import {useListQueryState} from 'src/hooks/useListQueryState';
import {useLiveChargerRefresh} from 'src/hooks/useLiveRefresh';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';

/**
 * The strip and the list are two cards on one page, spaced like every other pair.
 *
 * The top margin is the app bar's: the table used to be first and carried its own, and now that the
 * strip is above it the clearance has to live on the page, or the block sits under the bell.
 */
const pageSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    mt: 2.5,
};

type ChargersQueryTrigger = ReturnType<typeof chargersApi.useLazyGetChargersQuery>[0];

const DEFAULT_PAGE_SIZE = 100;

/**
 * Everything this screen can be looking at, and the only copy of it: each field lives in the query
 * string, so opening a charger and coming back restores the view instead of resetting it.
 *
 * `locationId`, `houseNumber`, `withoutDriver` and `withoutLocation` are deliberately absent - this
 * screen has no control that sets them, and a field nobody can change does not belong in the address
 * bar. They are still sent, as the absences they are.
 */
const QUERY_DEFAULTS = {
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    keyword: '',
    connectorStatus: '',
    chargePointStatus: '',
    city: '',
    country: '',
    street: '',
    model: '',
    vendor: '',
    sortField: ChargerSortFieldEnum.CREATED_AT as string,
    sortAsc: false,
};

interface DataType {
    [index: string]: unknown;
}

/**
 * The body both the list and the status strip are asked for.
 * <p>
 * Shared rather than written twice: the strip counts the set the list shows, and two copies of "which
 * filters go in" is exactly how the two come to disagree.
 */
export const buildChargersSearchData = (
    tableData: ChargePointSearchRequestData,
    businessId: number
): ChargePointSearchRequestData => {
    const newData: DataType = {
        ...tableData,
        businessId: tableData.businessId ? tableData.businessId : businessId,
    };
    Object.keys(tableData).map((item: string) => {
        !newData[item] && delete newData[item];
    });

    return {...newData};
};

export const handleGetChargers =
    (trigger: ChargersQueryTrigger, businessId: number) =>
        async (tableData: ChargePointSearchRequestData, params: ListParamsType) => {
            store.dispatch(preloaderChangeStatus(true));

            const data: ChargePointSearchRequestData = buildChargersSearchData(tableData, businessId);

            // No companyId from the caller's own account: the server forces it for anyone whose
            // reach is a company, and leaves it alone for anyone whose reach is wider - where it was
            // never theirs to set anyway.

            try {
                return await trigger({data, params}).unwrap();
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                store.dispatch(preloaderChangeStatus(false));
            }
        };

export const Chargers: FC = () => {
    // The business being worked in: the caller's own, or the one a platform administrator picked.
    // Reading `authInfo.business.id` directly was a crash for them - they have no business of
    // their own, and null has no id.
    const businessId = useBusinessId();
    const intl = useIntl();
    const persona = usePersona();
    const [open, setOpen] = useState<boolean>(false);
    const dialogTitle = intl.formatMessage({id: 'createNewCharger'});
    const {authInfo} = useAppSelector((state) => state.common);
    const isCompanyUser = persona.isCompany;
    const [trigger, response] = chargersApi.useLazyGetChargersQuery();
    const [query, setQuery] = useListQueryState(QUERY_DEFAULTS);

    // A boolean rather than `authInfo` itself: the effect below waits for the session to arrive, and
    // depending on the object would re-ask the server every time anything in it was replaced.
    const isSignedIn = Boolean(Object.keys(authInfo).length);

    // Both derived from the address bar rather than kept beside it. There used to be two `useState`
    // here and a mount effect that tried to reconstruct them from `location.state` and a regex over
    // `location.search`; the state a reload or a Back had already put in the URL was the one thing it
    // could not read.
    const filterParams: ChargePointSearchRequestData = useMemo(() => ({
        businessId: 0,
        chargePointStatus: query.chargePointStatus || undefined,
        connectorStatus: query.connectorStatus || undefined,
        city: query.city,
        country: query.country,
        street: query.street,
        model: query.model,
        vendor: query.vendor,
        keyword: query.keyword || undefined,
        locationId: undefined,
        withoutDriver: false,
        withoutLocation: false,
        sortField: query.sortField,
        sortAsc: query.sortAsc,
    }), [query]);

    const tableParams: ListParamsType = useMemo(
        () => ({page: query.page, size: query.size}),
        [query.page, query.size]
    );

    // Both status filters are left out on purpose: the server ignores them anyway, and keeping them
    // out of the key means clicking a tile does not refetch numbers that cannot have changed.
    const statusCountsRequest = useMemo(
        () => ({
            ...buildChargersSearchData(filterParams, businessId),
            connectorStatus: undefined,
            chargePointStatus: undefined,
        }),
        [filterParams, businessId]
    );
    const {data: statusCounts} = chargersApi.useGetConnectorStatusCountsQuery(statusCountsRequest, {
        skip: !Object.keys(authInfo).length,
    });

    // The numbers and the rows are one answer to one question - what is the state right now - and the
    // websocket already knows it changed. Both queries carry the `Chargers` tag, so one invalidation
    // brings the strip and the list back together; no preloader and no toast, because nobody asked
    // for this refresh and it must not interrupt whatever they are doing. A failed one simply leaves
    // the previous answer on screen until the next event.
    const dispatch = useAppDispatch();
    useLiveChargerRefresh(() => {
        dispatch(chargersApi.util.invalidateTags(['Chargers']));
    });

    const isRealEstate = useDefineIsRealEstateRole();

    const handleOpenModal = () => {
        setOpen(true);
    };
    const handleCloseModal = () => {
        setOpen(false);
        void callHandler(filterParams, tableParams);
    };

    /**
     * Whatever the list is now asking for, as a whole request body from the table below.
     *
     * Back to the first page, because the row that was on page four of the old filter is not on page
     * four of the new one. Only the fields the address bar carries are taken from it - the rest of the
     * body is this screen's own constant.
     */
    const handleChange = (value: ChargePointSearchRequestData) => {
        setQuery({
            ...query,
            page: 0,
            keyword: value.keyword ?? '',
            connectorStatus: String(value.connectorStatus ?? ''),
            chargePointStatus: String(value.chargePointStatus ?? ''),
            city: value.city ?? '',
            country: value.country ?? '',
            street: value.street ?? '',
            model: value.model ?? '',
            vendor: value.vendor ?? '',
            sortField: value.sortField ?? ChargerSortFieldEnum.CREATED_AT,
            sortAsc: value.sortAsc ?? false,
        });
    };

    // The two axes are alternatives, not filters to combine: picking a connector status answers
    // "show me these connectors", and leaving a station status standing beside it would answer a
    // question nobody asked. Each selection clears the other; clicking the active one clears both.
    const handleStatusTile = (status: ConnectorStatusEnum) => {
        setQuery({
            ...query,
            page: 0,
            chargePointStatus: '',
            connectorStatus: query.connectorStatus === status ? '' : status,
        });
    };

    const handleStationPill = (status: ChargePointStatus) => {
        setQuery({
            ...query,
            page: 0,
            connectorStatus: '',
            chargePointStatus: query.chargePointStatus === status ? '' : status,
        });
    };

    const handleChangePage = (page: number) => setQuery({...query, page});

    const handleChangeRowsNumber = (e: ChangeEvent<HTMLInputElement>) => setQuery({
        ...query,
        page: 0,
        size: +e.target.value || DEFAULT_PAGE_SIZE,
    });

    // Memoised so the effect below can depend on it: `trigger` is stable and the business only
    // changes when a platform administrator switches tenant, which has to re-ask anyway.
    const callHandler = useMemo(() => handleGetChargers(trigger, businessId), [trigger, businessId]);

    // The permission, and nothing beside it: real estate was excluded by name here, and so were
    // company, employee and driver by never being mentioned - all of which chargers.create already
    // says, for every role including the ones nobody listed.
    const canCreateCharger = useCan('chargers.create');

    /**
     * One place asks the server, whenever the address bar says something else.
     *
     * Every handler above only writes the URL. Before this each of them called the server itself and
     * a mount effect had to put the state back together from a preset and a regex - so arriving with
     * a filter already in the URL, which is what Back and a reload both are, fetched the unfiltered
     * list. A status handed over by another screen is now a query string like any other
     * (`/app/chargers?connectorStatus=CHARGING`), which survives that reload too.
     */
    useEffect(() => {
        if (!isSignedIn) {
            return;
        }
        void callHandler(filterParams, tableParams);
    }, [isSignedIn, callHandler, filterParams, tableParams]);

    return (
        <Box sx={pageSx}>
            <ConnectorStatusStrip
                data={statusCounts}
                activeStatus={filterParams.connectorStatus}
                onSelect={handleStatusTile}
                activeChargePointStatus={filterParams.chargePointStatus}
                onSelectChargePointStatus={handleStationPill}
            />
            {response.data && (
                <ChargerTable
                    data={response.data}
                    filters={filterParams}
                    pageSize={tableParams.size}
                    pageNumber={tableParams.page}
                    handleUpdateTableData={handleChange}
                    handleChangePage={handleChangePage}
                    handleChangeRowsNumber={handleChangeRowsNumber}
                    isCompanyUser={isCompanyUser}
                    title={intl.formatMessage({id: 'chargers'})}
                    onAddClick={handleOpenModal}
                    buttonText="addCharger"
                    onlyTitle={!canCreateCharger}
                    icon={<EvStationOutlined/>}
                />
            )}
            <AMPModal
                handleClose={handleCloseModal}
                open={open}
                title={dialogTitle}
                type="md"
                fullWidth
                withoutPaddings
            >
                <ChargerCreateFormWrapper
                    handleClose={handleCloseModal}
                    authInfo={authInfo}
                />
            </AMPModal>
        </Box>
    );
};
